import React, { useEffect, useState } from "react";
import MerchantLayout, { StatusPill } from "@/components/MerchantLayout";
import { mApi } from "@/lib/api";
import { ArrowClockwise } from "@phosphor-icons/react";

const COLORS = {
  delivered: "text-emerald-700 bg-emerald-50 border-emerald-200",
  pending: "text-amber-700 bg-amber-50 border-amber-200",
  failed: "text-rose-700 bg-rose-50 border-rose-200",
};

export default function MerchantWebhookEvents() {
  const [items, setItems] = useState([]);
  const [filter, setFilter] = useState("");
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(null);

  const load = () => {
    setLoading(true);
    mApi.get(`/merchant/webhook-events${filter ? `?status=${filter}` : ""}`)
      .then((r) => setItems(r.data.items || []))
      .finally(() => setLoading(false));
  };
  useEffect(load, [filter]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <MerchantLayout>
      <div className="p-6 lg:p-10" data-testid="merchant-webhook-events">
        <div className="mb-6 flex items-end justify-between gap-4">
          <div>
            <div className="text-[10px] uppercase tracking-[0.18em] text-zinc-500 mb-1">Observability</div>
            <h1 className="font-display text-3xl tracking-tight font-medium">Webhook deliveries</h1>
            <p className="text-sm text-zinc-600 mt-2 max-w-xl">
              Every outbound webhook from Blonix to your endpoint, including
              attempt history, response codes, and HMAC signing metadata.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <select
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
              data-testid="we-filter"
              className="border border-zinc-200 rounded-sm px-3 py-2 text-sm bg-white"
            >
              <option value="">All statuses</option>
              <option value="delivered">Delivered</option>
              <option value="pending">Pending</option>
              <option value="failed">Failed</option>
            </select>
            <button
              onClick={load}
              data-testid="we-refresh"
              className="inline-flex items-center gap-1.5 border border-zinc-200 rounded-sm px-3 py-2 text-sm bg-white hover:bg-zinc-50"
            >
              <ArrowClockwise size={14} /> Refresh
            </button>
          </div>
        </div>

        <div className="bg-white border border-zinc-200 rounded-sm overflow-hidden" data-testid="we-table-wrap">
          <table className="w-full text-sm">
            <thead className="bg-zinc-50 border-b border-zinc-200">
              <tr className="text-[10px] uppercase tracking-[0.05em] text-zinc-500">
                <th className="text-left py-2.5 px-4">Event</th>
                <th className="text-left py-2.5 px-4">Status</th>
                <th className="text-left py-2.5 px-4">Attempts</th>
                <th className="text-left py-2.5 px-4">Last code</th>
                <th className="text-left py-2.5 px-4">Created</th>
                <th className="text-left py-2.5 px-4">Delivered</th>
                <th className="text-left py-2.5 px-4">Target</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={7} className="text-center text-zinc-500 py-12">Loading…</td></tr>
              ) : items.length === 0 ? (
                <tr><td colSpan={7} className="text-center text-zinc-500 py-12">No webhook events yet.</td></tr>
              ) : items.map((ev) => (
                <tr
                  key={ev.id}
                  data-testid={`we-row-${ev.id}`}
                  onClick={() => setOpen(ev)}
                  className="border-b border-zinc-100 hover:bg-zinc-50 cursor-pointer"
                >
                  <td className="py-3 px-4 font-mono text-xs">{ev.event}</td>
                  <td className="py-3 px-4"><StatusPill value={ev.delivery_status} /></td>
                  <td className="py-3 px-4 tabular-nums">{ev.attempts ?? 0}</td>
                  <td className="py-3 px-4 tabular-nums">{ev.last_status_code ?? "—"}</td>
                  <td className="py-3 px-4 text-zinc-600">{new Date(ev.created_at).toLocaleString()}</td>
                  <td className="py-3 px-4 text-zinc-600">{ev.delivered_at ? new Date(ev.delivered_at).toLocaleString() : "—"}</td>
                  <td className="py-3 px-4 text-zinc-500 truncate max-w-xs">{ev.target_url || "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {open && (
          <div
            className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center p-6 z-50"
            onClick={() => setOpen(null)}
            data-testid="we-detail-modal"
          >
            <div
              className="bg-white rounded-sm border border-zinc-200 max-w-2xl w-full max-h-[80vh] overflow-auto p-6"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-center justify-between mb-4">
                <div>
                  <div className="text-[10px] uppercase tracking-[0.18em] text-zinc-500">Webhook event</div>
                  <div className="font-display text-xl tracking-tight font-medium">{open.event}</div>
                </div>
                <span className={`text-xs px-2 py-1 border rounded-sm ${COLORS[open.delivery_status] || ""}`}>
                  {open.delivery_status}
                </span>
              </div>
              <dl className="grid grid-cols-2 gap-3 text-sm mb-4">
                <Field label="Event ID"><span className="font-mono text-xs">{open.id}</span></Field>
                <Field label="Attempts">{open.attempts ?? 0}</Field>
                <Field label="Last status code">{open.last_status_code ?? "—"}</Field>
                <Field label="Last error">{open.last_error || "—"}</Field>
                <Field label="Created">{new Date(open.created_at).toLocaleString()}</Field>
                <Field label="Last attempt">{open.last_attempt_at ? new Date(open.last_attempt_at).toLocaleString() : "—"}</Field>
                <Field label="Next attempt">{open.next_attempt_at ? new Date(open.next_attempt_at).toLocaleString() : "—"}</Field>
                <Field label="Target URL"><span className="text-zinc-700 break-all">{open.target_url || "—"}</span></Field>
              </dl>
              <div>
                <div className="text-[10px] uppercase tracking-[0.05em] text-zinc-500 mb-1">Payload</div>
                <pre data-testid="we-payload" className="bg-zinc-50 border border-zinc-200 rounded-sm p-3 text-xs font-mono overflow-auto">
{JSON.stringify(open.payload || {}, null, 2)}
                </pre>
              </div>
              <div className="mt-5 text-xs text-zinc-500">
                Replay is admin-controlled. Contact support to manually replay a failed event.
              </div>
            </div>
          </div>
        )}
      </div>
    </MerchantLayout>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-[0.05em] text-zinc-500">{label}</div>
      <div className="text-zinc-900 mt-0.5 break-words">{children}</div>
    </div>
  );
}
